import React, { useState } from 'react';
import { X, Search, UserCheck } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { Contact } from '../types';

interface Props {
  agencyId: string;
  agencyName: string;
  onClose: () => void;
}

const AssociateContactModal: React.FC<Props> = ({ agencyId, agencyName, onClose }) => {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState('');

  const { data } = useQuery({
    queryKey: ['contacts-assoc-search', search],
    queryFn: () => api.searchContacts({ q: search, limit: 20, active_only: true }),
    enabled: search.trim().length > 1,
  });

  const results: Contact[] = (data?.data ?? []).filter((c: Contact) => c.agency_id !== agencyId);

  const associate = async (contact: Contact) => {
    const name = (contact as any).full_name || `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim() || 'this contact';
    if (!window.confirm(`Transfer ${name} to ${agencyName}? This will change their agency assignment.`)) return;
    setSaving(contact.id);
    setError('');
    try {
      await api.updateContact(contact.id, { agency_id: agencyId });
      qc.invalidateQueries({ queryKey: ['agency-contacts', agencyId] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to associate contact.');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="modal"
        onMouseDown={(e) => e.stopPropagation()}
        style={{ maxWidth: 500, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexShrink: 0 }}>
          <h3 style={{ margin: 0 }}>Associate Existing Contact</h3>
          <button className="btn btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <p style={{ fontSize: 14, color: 'var(--gray-500)', margin: '0 0 12px', flexShrink: 0 }}>
          Search for a contact from another agency and transfer them to <strong>{agencyName}</strong>.
        </p>

        <div style={{ position: 'relative', marginBottom: 8, flexShrink: 0 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} />
          <input
            className="field-input"
            style={{ paddingLeft: 32 }}
            autoFocus
            placeholder="Search contacts by name or agency…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 6 }}>
          {search.trim().length <= 1 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: 14 }}>
              Type at least 2 characters to search
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: 14 }}>
              No contacts found (already-associated contacts are hidden)
            </div>
          ) : (
            results.map((c) => {
              const displayName = (c as any).full_name
                || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim()
                || 'Unknown';
              return (
              <div
                key={c.id}
                style={{
                  padding: '10px 14px',
                  borderBottom: '1px solid var(--gray-100)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
                    {displayName}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                    {c.agency_acronym ? `${c.agency_acronym} — ${c.agency_name ?? ''}` : (c.agency_name ?? '—')}
                  </div>
                  {c.title && (
                    <div style={{ fontSize: 12, color: 'var(--gray-500)', fontStyle: 'italic' }}>{c.title}</div>
                  )}
                </div>
                <button
                  className="btn btn-secondary"
                  style={{ fontSize: 12, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
                  disabled={saving === c.id}
                  onClick={() => associate(c)}
                >
                  <UserCheck size={13} />
                  {saving === c.id ? 'Transferring…' : 'Transfer'}
                </button>
              </div>
              );
            })
          )}
        </div>

        {error && <p style={{ color: 'var(--red)', fontSize: 12, margin: '8px 0 0', flexShrink: 0 }}>{error}</p>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, flexShrink: 0 }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default AssociateContactModal;
